import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import {
  DEFAULT_PRODUCT,
  EMPTY_COLORS,
  isProductKey,
  type ColorKey,
  type Colors,
  type ProductKey,
} from "@/shared/config/widget";
import { getQueryParam } from "@/shared/lib/queryParams";
import { sanitizeChan, sanitizePartnerId } from "@/shared/lib/partnerParams";

interface State {
  colors: Colors;
  product: ProductKey;
  partnerId: string;
  /** affid пришёл из query-строки конструктора — поле не редактируется. */
  partnerIdLocked: boolean;
  b: string;
  chan: string;
  hidePromo: boolean;
  hideTitle: boolean;
  companies: string[];
  /** Пустой список СК означает «все», а снятый выбор — незаполненную настройку. */
  companiesCleared: boolean;
}

type Action =
  | { type: "setColor"; key: ColorKey; value: string }
  | { type: "setPartnerId"; value: string }
  | { type: "setChan"; value: string }
  | { type: "setHidePromo"; value: boolean }
  | { type: "setHideTitle"; value: boolean }
  | { type: "setProduct"; value: ProductKey }
  | { type: "setCompanies"; value: string[]; cleared: boolean }
  | { type: "reset" };

function createInitialState(): State {
  const product = getQueryParam("product");
  const queryPartnerId = sanitizePartnerId(getQueryParam("affid"));
  return {
    colors: { ...EMPTY_COLORS },
    product: isProductKey(product) ? product : DEFAULT_PRODUCT,
    partnerId: queryPartnerId,
    partnerIdLocked: Boolean(queryPartnerId),
    b: sanitizePartnerId(getQueryParam("b")),
    chan: sanitizeChan(getQueryParam("chan")),
    hidePromo: false,
    hideTitle: false,
    companies: [],
    companiesCleared: false,
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "setColor":
      return { ...state, colors: { ...state.colors, [action.key]: action.value } };
    case "setPartnerId":
      if (state.partnerIdLocked) return state;
      return { ...state, partnerId: sanitizePartnerId(action.value) };
    case "setChan":
      return { ...state, chan: sanitizeChan(action.value) };
    case "setHidePromo":
      return { ...state, hidePromo: action.value };
    case "setHideTitle":
      return { ...state, hideTitle: action.value };
    case "setProduct":
      return { ...state, product: action.value };
    case "setCompanies":
      return { ...state, companies: action.value, companiesCleared: action.cleared };
    case "reset":
      return { ...state, colors: { ...EMPTY_COLORS } };
    default:
      return state;
  }
}

interface ContextValue extends State {
  setColor: (key: ColorKey, value: string) => void;
  setPartnerId: (value: string) => void;
  setChan: (value: string) => void;
  setHidePromo: (value: boolean) => void;
  setHideTitle: (value: boolean) => void;
  setProduct: (value: ProductKey) => void;
  setCompanies: (value: string[], cleared: boolean) => void;
  reset: () => void;
}

const ConfiguratorContext = createContext<ContextValue | null>(null);

export function ConfiguratorProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, createInitialState);

  const value = useMemo<ContextValue>(
    () => ({
      ...state,
      setColor: (key, val) => dispatch({ type: "setColor", key, value: val }),
      setPartnerId: (val) => dispatch({ type: "setPartnerId", value: val }),
      setChan: (val) => dispatch({ type: "setChan", value: val }),
      setHidePromo: (val) => dispatch({ type: "setHidePromo", value: val }),
      setHideTitle: (val) => dispatch({ type: "setHideTitle", value: val }),
      setProduct: (val) => dispatch({ type: "setProduct", value: val }),
      setCompanies: (val, cleared) =>
        dispatch({ type: "setCompanies", value: val, cleared }),
      reset: () => dispatch({ type: "reset" }),
    }),
    [state],
  );

  return (
    <ConfiguratorContext.Provider value={value}>
      {children}
    </ConfiguratorContext.Provider>
  );
}

export function useConfigurator(): ContextValue {
  const ctx = useContext(ConfiguratorContext);
  if (!ctx) throw new Error("useConfigurator must be used within ConfiguratorProvider");
  return ctx;
}
